/**
 * ============================================================================
 * User 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 User 模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createUserApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createUserApi } from '@bayes-mes/shared/api/user/factory'
 * export const userApi = createUserApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await userApi.getUserList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createUserApi } from '@bayes-mes/shared/api/user/factory'
 * export const userApi = createUserApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/user/factory
 * @description User 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  User,                    // 主实体类型
  UserQueryParams,          // 列表查询参数
  CreateUserForm,           // 创建表单
  UpdateUserForm,           // 更新表单
} from '../../types/user'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 User 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 User 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 User 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createUserApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取用户列表（分页）
     * 
     * 使用场景：
     * - 在用户管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据各种条件查询数据
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页），page: 2（第二页）
     *   - per_page?: number - 每页数量，默认 20，最大 100
     *     示例：per_page: 20（每页20条）
     *   - keyword?: string - 搜索关键词，搜索用户名、姓名或邮箱
     *     示例：keyword: '张三'（搜索包含"张三"的记录）
     *   - username?: string - 用户名（精确匹配）
     *     示例：username: 'admin'
     *   - name?: string - 姓名（模糊匹配）
     *     示例：name: '张'（搜索姓张的用户）
     *   - phone?: string - 手机号（模糊匹配）
     *     示例：phone: '138'
     *   - email?: string - 邮箱（模糊匹配）
     *     示例：email: '@qq.com'
     *   - role_id?: number - 角色ID筛选
     *     示例：role_id: 1（筛选管理员角色）
     *   - status?: number - 状态筛选
     *     示例：status: 1（只显示正常用户）
     *   - sort_by?: string - 排序字段
     *     示例：sort_by: 'created_at'（按创建时间排序）
     *   - sort_order?: 'asc' | 'desc' - 排序方向
     *     示例：sort_order: 'desc'（降序）
     * 
     * @returns Promise<PaginationResult<User>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     data: User[],        // 用户列表数组，每个元素是一个用户对象
     *     total: number,           // 总记录数，如 158（共有158条记录）
     *     current_page: number,    // 当前页码，如 2（正在查看第2页）
     *     per_page: number,        // 每页数量，如 20（每页显示20条）
     *     last_page: number,       // 最后一页页码，如 8（总共8页）
     *     from: number,            // 当前页起始记录号，如 21（从第21条开始）
     *     to: number               // 当前页结束记录号，如 40（到第40条结束）
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在页面上点击"下一页"按钮或输入搜索关键词
     * 2. 前端调用：组件调用 userApi.getUserList({ page: 2, keyword: '张三' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/system/users?page=2&keyword=张三
     * 5. 后端处理：
     *    - 路由匹配到 UserController@index 方法
     *    - 验证查询参数（UserQueryParams）
     *    - 查询数据库，应用筛选条件和分页
     *    - 通过 UserResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<User> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await userApi.getUserList()
     * console.log(page1.data)  // User对象数组
     * console.log(page1.total) // 总记录数
     * 
     * // 示例2：获取第二页，每页20条
     * const page2 = await userApi.getUserList({
     *   page: 2,
     *   per_page: 20
     * })
     * 
     * // 示例3：搜索功能
     * const searchResult = await userApi.getUserList({
     *   keyword: '张三'
     * })
     * 
     * // 示例4：组合查询（分页 + 搜索 + 排序 + 筛选）
     * const result = await userApi.getUserList({
     *   page: 1,
     *   per_page: 10,
     *   keyword: '张',
     *   status: 1,
     *   role_id: 2,
     *   sort_by: 'created_at',
     *   sort_order: 'desc'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getUserList(params?: UserQueryParams) {
      // 调用请求客户端的 get 方法
      // - 第一个参数：从 routes.ts 引用的 URL 路径
      // - 第二个参数：配置对象，{ params } 会被转换为 URL 查询参数
      // - 泛型参数：指定返回数据的类型
      return client.get<PaginationResult<User>>(API_ROUTES.user.list, { params })
    },
    
    /**
     * 获取用户详情
     * 
     * 使用场景：
     * - 在用户详情页面显示完整信息
     * - 编辑用户时获取当前数据
     * - 查看用户的详细资料
     * 
     * @param id - 用户的唯一标识符（主键）
     *   示例：id: 123（获取 ID 为 123 的用户）
     * 
     * @returns Promise<User> 单个用户对象
     *   返回的数据结构：
     *   {
     *     id: number,              // 用户 ID
     *     username: string,        // 用户名
     *     name: string,            // 姓名
     *     nickname: string | null, // 昵称
     *     email: string | null,    // 邮箱
     *     phone: string | null,    // 手机号
     *     avatar: string | null,   // 头像URL
     *     status: number,          // 状态
     *     roles: string[],         // 角色ID列表
     *     roleNames: string[],     // 角色名称列表
     *     // ... 用户的所有字段
     *     createdAt: string,       // 创建时间（ISO 8601格式）
     *     updatedAt: string        // 更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户点击列表中的某一行，或在 URL 中输入详情页地址
     * 2. 前端调用：userApi.getUserDetail(123)
     * 3. 发送请求：GET /api/v1/system/users/123
     * 4. 后端处理：UserController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的记录
     *    - 如果不存在，返回 404
     *    - 通过 UserResource 格式化数据
     * 5. 返回数据：单个 User 对象
     * 6. 前端显示：在详情页或编辑表单中展示数据
     * 
     * 代码示例：
     * ```typescript
     * // 获取 ID 为 123 的用户
     * const detail = await userApi.getUserDetail(123)
     * console.log(detail.name)  // 用户姓名
     * console.log(detail.username)  // 用户名
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const userData = await userApi.getUserDetail(Number(id))
     * ```
     * 
     * 错误处理：
     * - 404：用户不存在
     * - 403：无权限访问该用户
     * - 422：ID 格式错误（不是数字）
     */
    getUserDetail(id: number) {
      // 使用函数式路由，自动拼接 ID
      // routes.ts 中定义：detail: (user) => `/api/v1/users/${user}`
      return client.get<User>(API_ROUTES.user.detail(id))
    },
    
    /**
     * 创建新用户
     * 
     * 使用场景：
     * - 在用户管理页面点击"新建"按钮
     * - 提交创建用户的表单
     * - 批量导入时创建多个用户
     * 
     * @param data - 创建用户的表单数据
     *   字段要求（参考 CreateUserForm 类型）：
     *   - username: string - 用户名（必填，唯一，最大50字符）
     *   - name: string - 姓名（必填，最大255字符）
     *   - password: string - 密码（必填，最小6字符）
     *   - password_confirmation: string - 确认密码（必填，需与password一致）
     *   - nickname?: string - 昵称（可选，最大100字符）
     *   - email?: string - 邮箱（可选，需符合邮箱格式，唯一）
     *   - phone?: string - 手机号（可选，最大20字符，唯一）
     *   - avatar?: string - 头像URL（可选）
     *   - status?: number - 状态（可选，0禁用 1正常，默认1）
     *   - roles?: string[] - 角色ID列表（可选）
     * 
     * @returns Promise<User> 创建成功后的用户对象（包含生成的 ID）
     *   返回的数据：
     *   {
     *     id: number,          // 自动生成的 ID
     *     username: string,    // 提交的用户名
     *     name: string,        // 提交的姓名
     *     // ... 其他字段
     *     createdAt: string,   // 服务器生成的创建时间
     *     updatedAt: string    // 服务器生成的更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户填写创建表单，点击"提交"按钮
     * 2. 前端验证：表单验证（必填项、格式等）
     * 3. 前端调用：userApi.createUser({ username: 'test', name: '测试', ... })
     * 4. 发送请求：POST /api/v1/system/users
     *    请求体：{ username: 'test', name: '测试', ... }（JSON格式）
     * 5. 后端处理：UserController@store 方法
     *    - 验证数据（使用 CreateUserRequest）
     *    - 检查唯一性（用户名、邮箱、手机号不能重复）
     *    - 加密密码
     *    - 插入数据库
     *    - 分配角色
     *    - 返回新创建的记录（包含自动生成的 ID 和时间戳）
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 跳转到列表页或详情页
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 创建新用户
     * const newData: CreateUserForm = {
     *   username: 'testuser',
     *   name: '测试用户',
     *   password: '123456',
     *   password_confirmation: '123456',
     *   email: 'test@example.com',
     *   phone: '13800138000',
     *   status: 1,
     *   roles: ['1', '2']
     * }
     * 
     * const created = await userApi.createUser(newData)
     * console.log(created.id)  // 新创建的 ID
     * 
     * // 在表单提交中使用
     * const handleSubmit = async (formData: CreateUserForm) => {
     *   try {
     *     const result = await userApi.createUser(formData)
     *     message.success('创建成功')
     *     router.push(`/user/${result.id}`)  // 跳转到详情页
     *   } catch (error) {
     *     message.error('创建失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - 必填字段缺失
     *   - 字段格式错误
     *   - 用户名重复（违反唯一性约束）
     *   - 邮箱或手机号重复
     *   - 密码确认不一致
     * - 403：无权限创建
     * - 500：服务器错误（数据库插入失败）
     */
    createUser(data: CreateUserForm) {
      // POST 请求，第二个参数是请求体数据
      // 后端会自动将 snake_case 字段转换为数据库字段
      return client.post<User>(API_ROUTES.user.create, data)
    },
    
    /**
     * 更新用户
     * 
     * 使用场景：
     * - 在用户编辑页面修改后提交
     * - 更新用户的部分字段
     * - 修改用户状态或角色
     * 
     * @param id - 要更新的用户的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     *   字段要求（参考 UpdateUserForm 类型）：
     *   - 所有字段都是可选的（部分更新）
     *   - username?: string - 用户名（可选，唯一，最大50字符）
     *   - name?: string - 姓名（可选，最大255字符）
     *   - nickname?: string - 昵称（可选，最大100字符）
     *   - email?: string - 邮箱（可选，需符合邮箱格式，唯一）
     *   - phone?: string - 手机号（可选，最大20字符，唯一）
     *   - password?: string - 密码（可选，最小6字符，需提供password_confirmation）
     *   - password_confirmation?: string - 确认密码（可选）
     *   - avatar?: string - 头像URL（可选）
     *   - status?: number - 状态（可选，0禁用 1正常）
     *   - roles?: string[] - 角色ID列表（可选）
     *   - 不能修改的字段：id, createdAt（后端会忽略）
     * 
     * @returns Promise<User> 更新后的完整用户对象
     * 
     * 数据流向：
     * 1. 用户在编辑页面修改字段，点击"保存"
     * 2. 前端调用：userApi.updateUser(123, { name: '新名称' })
     * 3. 发送请求：PUT /api/v1/system/users/123
     *    请求体：{ name: '新名称' }（只包含修改的字段）
     * 4. 后端处理：UserController@update 方法
     *    - 验证 ID 是否存在
     *    - 验证更新数据（使用 UpdateUserRequest）
     *    - 更新数据库记录
     *    - 同步角色（如果提供了roles字段）
     *    - 返回更新后的完整数据
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的数据
     * 
     * 代码示例：
     * ```typescript
     * // 更新用户的姓名
     * const updated = await userApi.updateUser(123, {
     *   name: '新名称'
     * })
     * 
     * // 更新多个字段
     * const updated2 = await userApi.updateUser(123, {
     *   name: '新名称',
     *   status: 1,
     *   roles: ['1', '3']
     * })
     * 
     * // 修改密码
     * const updated3 = await userApi.updateUser(123, {
     *   password: 'newpassword123',
     *   password_confirmation: 'newpassword123'
     * })
     * 
     * // 在编辑表单中使用
     * const handleUpdate = async (id: number, formData: UpdateUserForm) => {
     *   try {
     *     const result = await userApi.updateUser(id, formData)
     *     message.success('更新成功')
     *     // 更新本地数据
     *     userData.value = result
     *   } catch (error) {
     *     message.error('更新失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：用户不存在
     * - 422：数据验证失败
     *   - 字段格式错误
     *   - 用户名重复（违反唯一性约束）
     *   - 密码确认不一致
     * - 403：无权限修改
     * - 409：冲突（如用户名、邮箱或手机号重复）
     */
    updateUser(id: number, data: UpdateUserForm) {
      // PUT 请求，用于完整或部分更新资源
      // 第一个参数：使用函数式路由拼接 ID
      // 第二个参数：更新的数据
      return client.put<User>(API_ROUTES.user.users(id), data)
    },
    
    /**
     * 删除用户
     * 
     * 使用场景：
     * - 在用户列表中点击"删除"按钮
     * - 在用户详情页删除当前记录
     * - 批量删除多个用户
     * 
     * @param id - 要删除的用户的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击删除按钮，弹出确认对话框
     * 2. 用户确认删除
     * 3. 前端调用：userApi.deleteUser(123)
     * 4. 发送请求：DELETE /api/v1/system/users/123
     * 5. 后端处理：UserController@destroy 方法
     *    - 验证 ID 是否存在
     *    - 检查是否可以删除（可能有业务规则限制）
     *    - 删除记录（软删除或硬删除）
     *    - 记录审计日志
     *    - 返回成功响应
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 从列表中移除该记录
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 删除指定用户
     * await userApi.deleteUser(123)
     * console.log('删除成功')
     * 
     * // 在列表中使用（带确认）
     * const handleDelete = async (id: number) => {
     *   const confirmed = await showConfirm('确定要删除该用户吗？')
     *   if (!confirmed) return
     *   
     *   try {
     *     await userApi.deleteUser(id)
     *     message.success('删除成功')
     *     // 刷新列表
     *     await loadList()
     *   } catch (error) {
     *     message.error('删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：用户不存在（可能已被删除）
     * - 403：无权限删除
     * - 409：无法删除（存在关联数据）
     *   例如：该用户创建的数据仍存在，需要先处理关联数据
     */
    deleteUser(id: number) {
      // DELETE 请求，用于删除资源
      // 通常不返回数据，或返回简单的成功消息
      return client.delete<void>(API_ROUTES.user.delete(id))
    },
  }
}

